import { ref, reactive, computed, readonly } from 'vue'
import { authApi, authStateManager, authUtils } from './auth'
import { apiRequest } from './request'
import { useUserStore } from '@/stores/user'

import type { 
  LoginRequest, 
  LoginResult, 
  AuthUserInfo, 
  JwtUserInfo,
  SmsLoginRequest,
  SendSmsCodeRequest,
  PlatformSmsLoginRequest
} from './auth'

// 认证状态Hook
export function useAuth() {
  // 使用 user store
  const userStore = useUserStore()
  
  // 响应式状态
  const loading = ref(false)
  const error = ref<string | null>(null)

  // 初始化状态
  const initAuthState = () => {
    userStore.initUserState()
  }

  // 登录方法
  const login = async (loginRequest: LoginRequest): Promise<LoginResult | null> => {
    loading.value = true
    error.value = null

    try {
      const loginResult = await authUtils.handleLogin(loginRequest)
      
      return loginResult
    } catch (err: any) {
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  // 短信验证码登录方法
  const loginWithSms = async (smsLoginRequest: PlatformSmsLoginRequest): Promise<LoginResult | null> => {
    loading.value = true
    error.value = null

    try {
      const response = await authApi.platformLoginWithSms(smsLoginRequest)
      
      if (!response.success) {
        throw new Error(response.message || '短信验证码登录失败')
      }

      const loginResult = response.data
      
      // 设置token和refreshToken
      apiRequest.setToken(loginResult.accessToken)
      if (loginResult.refreshToken) {
        apiRequest.setRefreshToken(loginResult.refreshToken)
      }
      
      // 设置当前用户信息
      if (loginResult.userInfo) {
        authStateManager.setCurrentUser({
          userId: loginResult.userInfo.userId,
          userName: loginResult.userInfo.userName,
          roles: loginResult.userInfo.roles,
          permissions: loginResult.userInfo.permissions,
          expiresAt: loginResult.expiresAt
        })
      }
      
      return loginResult
    } catch (err: any) {
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  // 发送短信验证码
  const sendSmsCode = async (phoneNumber: string): Promise<boolean> => {
    try {
      const response = await authApi.sendSmsCode({ phoneNumber })
      
      if (!response.success) {
        throw new Error(response.message || '发送验证码失败')
      }

      return true
    } catch (err: any) {
      error.value = err.message
      throw err
    }
  }

  // 登出方法
  const logout = async (): Promise<void> => {
    loading.value = true
    error.value = null

    try {
      await authUtils.handleLogout()
    } catch (err: any) {
      error.value = err.message
      console.error('登出失败:', err)
    } finally {
      loading.value = false
    }
  }

  // 获取用户信息
  const getProfile = async (): Promise<JwtUserInfo | null> => {
    const result = await userStore.fetchUserInfo()
    return result || null
  }

  // 手动获取并输出用户信息（用于调试）
  const fetchAndLogUserInfo = async () => {
    const result = await userStore.refreshUserInfo()
    return result || null
  }

  // 清除错误
  const clearError = () => {
    error.value = null
  }

  return {
    // 状态
    loading: readonly(loading),
    error: readonly(error),
    currentUser: userStore.currentUser,
    isLoggedIn: userStore.isLoggedIn,
    
    // 计算属性
    userName: userStore.userName,
    userRoles: userStore.userRoles,
    userPermissions: userStore.userPermissions,
    
    // 方法
    login,
    loginWithSms,
    sendSmsCode,
    logout,
    getProfile,
    fetchAndLogUserInfo,
    clearError,
    initAuthState
  }
}

// 认证守卫Hook
export function useAuthGuard() {
  const userStore = useUserStore()

  // 检查是否需要登录
  const requireAuth = () => {
    if (!userStore.isLoggedIn) {
      // 跳转到登录页
      window.location.href = '/login'
      return false
    }
    return true
  }

  // 检查是否有特定权限
  const requirePermission = (permission: string) => {
    if (!userStore.isLoggedIn) {
      window.location.href = '/login'
      return false
    }
    
    return userStore.hasPermission(permission)
  }

  return {
    requireAuth,
    requirePermission
  }
} 